import React, { useEffect, useMemo, useState } from "react";

const TagLinkRow = ({ link, tag, onUpdate, onDelete }) => {
  const [order, setOrder] = useState(link.order ?? 0);
  const [displayTitle, setDisplayTitle] = useState(link.display_title || "");
  const [minValue, setMinValue] = useState(link.min_value ?? "");
  const [maxValue, setMaxValue] = useState(link.max_value ?? "");
  const [unit, setUnit] = useState(link.unit || "");
  useEffect(() => {
    setOrder(link.order ?? 0);
    setDisplayTitle(link.display_title || "");
    setMinValue(link.min_value ?? "");
    setMaxValue(link.max_value ?? "");
    setUnit(link.unit || "");
  }, [link]);
  const optionalNumber = (value) => value === "" ? null : Number(value);
  return <div className="pb-member-row pb-member-row--tag">
    <span>{tag?.name || `Tag ${link.tag}`}</span>
    <label>Order<input className="pb-input" type="number" min="0" value={order} onChange={(e) => setOrder(e.target.value)} /></label>
    <label>Display title<input className="pb-input" value={displayTitle} onChange={(e) => setDisplayTitle(e.target.value)} placeholder={tag?.name || "Tag title"} /></label>
    <label>Min<input className="pb-input" type="number" step="any" value={minValue} onChange={(e) => setMinValue(e.target.value)} /></label>
    <label>Max<input className="pb-input" type="number" step="any" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} /></label>
    <label>Unit<input className="pb-input" value={unit} onChange={(e) => setUnit(e.target.value)} /></label>
    <button className="pb-action-btn" type="button" onClick={() => onUpdate(link.id, { order: Number(order) || 0, display_title: displayTitle.trim(), min_value: optionalNumber(minValue), max_value: optionalNumber(maxValue), unit: unit.trim() })}>Save</button>
    <button className="pb-action-btn is-danger" type="button" onClick={() => window.confirm("Remove this tag from the section?") && onDelete(link.id)}>Remove</button>
  </div>;
};

const ExistingSectionsList = ({ sections, tags, sectionTags, setMessage, onUpdate, onDelete, onAddTag, onUpdateTag, onDeleteTag }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ title: "", description: "", mode: "analog" });
  const [newTagId, setNewTagId] = useState("");
  const [newOrder, setNewOrder] = useState(0);

  const tagById = useMemo(() => new Map(tags.map((row) => [String(row.id), row])), [tags]);
  const links = sectionTags.filter((link) => String(link.section) === String(editingId));
  const linkedIds = new Set(links.map((link) => String(link.tag)));
  const availableTags = tags.filter((tag) => !linkedIds.has(String(tag.id)));

  const beginEdit = (section) => {
    setEditingId(section.id);
    setDraft({ title: section.title || "", description: section.description || "", mode: section.mode || "analog" });
    setNewTagId("");
    setNewOrder(0);
  };

  const save = async (sectionId) => {
    if (!draft.title.trim()) return setMessage("Section title is required.");
    if (await onUpdate(sectionId, { ...draft, title: draft.title.trim(), description: draft.description.trim() })) setEditingId(null);
  };

  const addTag = async () => {
    if (!newTagId) return setMessage("Choose a tag to add.");
    if (await onAddTag({ section: editingId, tag: Number(newTagId), order: Number(newOrder) || 0 })) {
      setNewTagId("");
      setNewOrder(0);
    }
  };

  return <div className="pb-card pb-list-card pb-list-card--sections"><div className="pb-list-card-heading"><div><span className="pb-list-card-kicker">Sections</span><h2>Existing Sections</h2></div><span className="pb-count-badge">{sections.length}</span></div><div className="pb-table-wrap"><table className="pb-table pb-table--sections">
    <thead><tr><th>Section</th><th>Mode</th><th>Description</th><th>Tags</th><th>Actions</th></tr></thead>
    <tbody>
      {sections.map((section, index) => {
        const editing = editingId === section.id;
        const tagCount = sectionTags.filter((link) => String(link.section) === String(section.id)).length;
        return <React.Fragment key={section.id}>
          <tr className={`pb-data-row ${index % 2 ? "is-alt" : ""}`}>
            <td className="pb-table-title">{section.title}</td>
            <td><span className="pb-badge">{section.mode}</span></td>
            <td className="pb-table-description">{section.description || "—"}</td>
            <td>{tagCount}</td>
            <td><div className="pb-table-actions"><button className="pb-action-btn is-secondary" type="button" onClick={() => editing ? setEditingId(null) : beginEdit(section)}>{editing ? "Close" : "Edit"}</button><button className="pb-action-btn is-danger" type="button" onClick={() => window.confirm(`Delete section “${section.title}”? Tags will not be deleted.`) && onDelete(section.id)}>Delete</button></div></td>
          </tr>
          {editing && <tr className="pb-edit-row"><td colSpan="5"><div className="pb-edit-panel">
            <div className="pb-edit-fields pb-edit-fields--section"><input className="pb-input" value={draft.title} onChange={(e) => setDraft((v) => ({ ...v, title: e.target.value }))} aria-label="Section title" /><textarea className="pb-textarea" value={draft.description} onChange={(e) => setDraft((v) => ({ ...v, description: e.target.value }))} aria-label="Section description" /><select className="pb-select" value={draft.mode} onChange={(e) => setDraft((v) => ({ ...v, mode: e.target.value }))} aria-label="Section mode"><option value="analog">Analog</option><option value="digital">Digital</option><option value="io">IO</option></select></div>
            <h3 className="pb-member-heading">Tags in this section</h3>
            {links.map((link) => <TagLinkRow key={link.id} link={link} tag={tagById.get(String(link.tag))} onUpdate={onUpdateTag} onDelete={onDeleteTag} />)}
            {links.length === 0 && <div className="pb-muted">No tags in this section.</div>}
            <div className="pb-add-member"><select className="pb-select" value={newTagId} onChange={(e) => setNewTagId(e.target.value)}><option value="">Add a tag…</option>{availableTags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}</select><input className="pb-input" type="number" min="0" value={newOrder} onChange={(e) => setNewOrder(e.target.value)} aria-label="New tag order" /><button className="pb-action-btn" type="button" onClick={addTag}>Add tag</button></div>
            <div className="pb-item-actions"><button className="pb-action-btn" type="button" onClick={() => save(section.id)}>Save section</button><button className="pb-action-btn is-secondary" type="button" onClick={() => setEditingId(null)}>Close</button></div>
          </div></td></tr>}
        </React.Fragment>;
      })}
      {sections.length === 0 && <tr><td colSpan="5" className="pb-muted">No sections yet.</td></tr>}
    </tbody>
  </table></div></div>;
};

export default ExistingSectionsList;
